/**
 * "Add this to my phone" — Step 175.
 *
 * Kai: *"something more automated to add it as an app? Or is the easiest way
 * to share, add to home screen?"*
 *
 * The honest answer is both, and which one you get is decided by the browser
 * rather than by us:
 *
 *  - **Chrome, Edge, Android** fire `beforeinstallprompt`. That event can be
 *    saved and replayed on a click, which is a real one-tap install. It is the
 *    automated answer and it is what this renders when it is available.
 *  - **Safari, and therefore every iPhone**, has no such event and no API. The
 *    only route is Share → Add to Home Screen, by hand. Apple has not shipped
 *    an alternative, so anything claiming to automate it on iOS is showing
 *    instructions too — this one just says so.
 *
 * It renders NOTHING when the app is already installed, and nothing in a
 * browser that cannot install at all, rather than offering a button that does
 * not work. A dismissal is remembered, because a prompt you have already said
 * no to is an advert.
 */
import { useEffect, useState } from 'react';
import { Share, Plus, Download, X } from 'lucide-react';
import { Button } from '../ui/button';

/** The event Chrome fires. Not in lib.dom, because it is not standard. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED = 'itsm.install-dismissed';

function alreadyInstalled(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS's own flag, which predates the standard and is still the only one
    // Safari sets.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function InstallApp({ variant = 'banner' }: { variant?: 'banner' | 'button' }) {
  const [event, setEvent] = useState<InstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED) === '1';
    } catch {
      return false; // private browsing: offering twice beats never offering
    }
  });
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Stop the browser's own mini-infobar so there are not two offers.
      e.preventDefault();
      setEvent(e as InstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  if (alreadyInstalled() || dismissed) return null;
  // Nothing to offer: not iOS, and no install event. A desktop Firefox user
  // gets no button rather than a button that does nothing.
  if (!event && !isIos()) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED, '1');
    } catch {
      /* nothing to remember it with; it will ask again next visit */
    }
  };

  const install = async () => {
    if (event) {
      await event.prompt();
      const { outcome } = await event.userChoice;
      if (outcome === 'accepted') dismiss();
      setEvent(null);
      return;
    }
    setShowIosHelp((v) => !v);
  };

  if (variant === 'button') {
    return (
      <Button variant="ghost" size="sm" onClick={() => void install()}>
        <Download className="h-4 w-4" />
        Install
      </Button>
    );
  }

  return (
    <div className="safe-x mx-auto mb-3 max-w-3xl rounded-lg border bg-card p-3 text-sm">
      <div className="flex items-start gap-3">
        <Download className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">Add this to your home screen</p>
          <p className="text-xs text-muted-foreground">
            It opens like an app, full screen, and remembers you are signed in.
          </p>
          {showIosHelp && (
            <ol className="mt-2 space-y-1 text-xs text-muted-foreground">
              <li className="flex items-center gap-1.5">
                1. Tap <Share className="h-3.5 w-3.5" /> Share, at the bottom of Safari
              </li>
              <li className="flex items-center gap-1.5">
                2. Choose <Plus className="h-3.5 w-3.5" /> Add to Home Screen
              </li>
              <li>3. Tap Add</li>
            </ol>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button size="sm" onClick={() => void install()}>
            {event ? 'Install' : showIosHelp ? 'Hide' : 'How'}
          </Button>
          <Button variant="ghost" size="icon" aria-label="Not now" onClick={dismiss}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
